import { ApiError } from "api/client";
import { uploadImage } from "api/images";
import { changeTreeVisibility, updateTree } from "api/trees";
import { CreateTreeDialog } from "pages/HomePage/components";
import { defaultCoverPreview } from "pages/HomePage/data";
import type { NewTreeForm, Status } from "pages/HomePage/models";
import React, { type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { compressImage } from "utilities";

import DeleteTreeDialog from "./DeleteTreeDialog";
import type { EditTreeDialogProps } from "./editTreeDialog.types";

/** Container for editing an existing tree's metadata (title, description, cover, visibility, comments). */
const EditTreeDialog = ({
	canCreatePrivateTrees,
	canDelete,
	onClose,
	onDeleted,
	onSaved,
	tree
}: EditTreeDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();

	const [form, setForm] = useState<NewTreeForm>({
		commentsWhenPublic: tree.commentsEnabled,
		description: tree.description ?? "",
		title: tree.title,
		visibility: tree.visibility as Status
	});
	const [coverPreview, setCoverPreview] = useState(tree.coverImageUrl?.trim() || defaultCoverPreview);
	const [coverFile, setCoverFile] = useState<File | null>(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	const onFormChange = useCallback((updates: Partial<NewTreeForm>): void => {
		setForm((current: NewTreeForm) => ({ ...current, ...updates }));
	}, []);

	const onCoverSelect = useCallback((file: File): void => {
		setCoverFile(file);

		const reader = new FileReader();
		reader.onload = (): void => {
			if (typeof reader.result === "string") {
				setCoverPreview(reader.result);
			}
		};
		reader.readAsDataURL(file);
	}, []);

	const openDeleteDialog = useCallback((): void => setIsDeleteOpen(true), []);
	const closeDeleteDialog = useCallback((): void => setIsDeleteOpen(false), []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const title = form.title.trim();
			if (!title) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setErrorMessage("");
			setIsSubmitting(true);

			try {
				let coverImageUrl: string | undefined;
				let coverImageName: string | undefined;
				if (coverFile) {
					// Same treatment as node images: re-encoding through a canvas resizes the cover and drops
					// the Exif block with it, so a family photo does not publish the coordinates it was taken
					// at on a tree anyone can open.
					const uploaded = await uploadImage(await compressImage(coverFile), "trees", token);
					coverImageUrl = uploaded.url;
					coverImageName = uploaded.name;
				}

				// Save title/description/comments/cover; omitting the cover preserves the existing one server-side.
				let saved = await updateTree(
					tree.id,
					{
						title,
						description: form.description.trim() || undefined,
						commentsEnabled: form.commentsWhenPublic,
						coverImageUrl,
						coverImageName
					},
					token
				);

				// Visibility is a separate endpoint (and Pro-gated); only call it when it actually changed.
				if (form.visibility !== tree.visibility) {
					saved = await changeTreeVisibility(tree.id, form.visibility, token);
				}

				onSaved(saved);
				onClose();
			} catch (error) {
				setErrorMessage(error instanceof ApiError ? error.message : t("editDialog.saveError"));
			} finally {
				setIsSubmitting(false);
			}
		},
		[coverFile, form, getAccessToken, onClose, onSaved, t, tree.id, tree.visibility]
	);

	return (
		<>
			<CreateTreeDialog
				canCreatePrivateTrees={canCreatePrivateTrees}
				coverPreview={coverPreview}
				deleteLabel={t("editDialog.delete")}
				errorMessage={errorMessage}
				form={form}
				heading={t("editDialog.title")}
				isDismissBlocked={isDeleteOpen}
				isSubmitting={isSubmitting}
				onClose={onClose}
				onCoverSelect={onCoverSelect}
				onDelete={canDelete ? openDeleteDialog : undefined}
				onFormChange={onFormChange}
				onSubmit={onSubmit}
				subheading={t("editDialog.description")}
				submitLabel={t("editDialog.save")}
				submittingLabel={t("editDialog.saving")}
			/>

			{isDeleteOpen && (
				<DeleteTreeDialog
					entityCount={tree.entityCount}
					onCancel={closeDeleteDialog}
					onDeleted={onDeleted}
					treeId={tree.id}
					treeTitle={tree.title}
				/>
			)}
		</>
	);
};

export default EditTreeDialog;
